import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Search, Calendar, Clock, Tag,
  MessageCircle, Heart, Share2, BookOpen, TrendingUp,
  ArrowRight, Send, X, Loader2, Link2, Check, ChevronRight, Eye,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';
import usePageMeta from '../lib/usePageMeta';
import { sanitizeBlogHTML } from '../utils/sanitize';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Blog List Component
const BlogList = () => {
  const navigate = useNavigate();
  usePageMeta('Blog', 'Articles from the iVisora team.', { path: '/blog' });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [posts, setPosts] = useState([]);
  const [apiCategories, setApiCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!API_URL) { setLoading(false); return; }
    Promise.all([
      fetch(`${API_URL}/api/public/blog?limit=50`).then(r => r.ok ? r.json() : []),
      fetch(`${API_URL}/api/public/categories?type=blog`).then(r => r.ok ? r.json() : []),
    ]).then(([postsData, catsData]) => {
      setPosts(Array.isArray(postsData) ? postsData : []);
      setApiCategories(Array.isArray(catsData) ? catsData : []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const all = { id: 'All', name: 'All', count: posts.length };
    const byCat = apiCategories.map(c => ({
      id: c.id,
      name: c.name,
      count: posts.filter(p => p.category_id === c.id).length,
    }));
    return [all, ...byCat];
  }, [posts, apiCategories]);

  const filteredPosts = useMemo(() => {
    return posts.filter(post => {
      const matchesSearch = !searchQuery ||
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (post.excerpt || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (post.tags || []).some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesCategory = selectedCategory === 'All' ||
        apiCategories.find(c => c.id === selectedCategory)?.name === apiCategories.find(c => c.id === post.category_id)?.name;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory, posts, apiCategories]);

  const featuredPosts = posts.filter(p => p.is_featured);

  const getCategoryName = (categoryId) =>
    apiCategories.find(c => c.id === categoryId)?.name || '';

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-600 text-white py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge className="bg-white/20 text-white border-white/30">
              <BookOpen className="w-3 h-3 mr-1" /> iVisora Blog
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold">
              Design Tips, Tutorials & Inspiration
            </h1>
            <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto">
              Learn from industry experts and stay up-to-date with the latest design trends
            </p>
            
            {/* Search Bar */}
            <div className="max-w-xl mx-auto mt-8">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-14 text-lg bg-white text-gray-900 border-0 rounded-xl shadow-xl"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      {!searchQuery && selectedCategory === 'All' && featuredPosts.length > 0 && (
        <section className="container mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-6">
            {featuredPosts.slice(0, 2).map((post) => (
              <Card
                key={post.id}
                className="overflow-hidden cursor-pointer hover:shadow-2xl transition-all group"
                onClick={() => navigate(`/blog/${post.id}`)}
              >
                <div className="relative h-64 overflow-hidden">
                  {post.thumbnail && (
                    <img src={post.thumbnail} alt={post.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <Badge className="absolute top-4 left-4 bg-purple-600 text-white">
                    <TrendingUp className="w-3 h-3 mr-1" /> Featured
                  </Badge>
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    {getCategoryName(post.category_id) && (
                      <Badge variant="secondary" className="mb-2">{getCategoryName(post.category_id)}</Badge>
                    )}
                    <h3 className="text-xl font-bold mb-2">{post.title}</h3>
                    <div className="flex items-center gap-4 text-sm text-white/80">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(post.published_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {post.read_time} min read
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="w-full lg:w-72 flex-shrink-0 space-y-6">
            {/* Categories */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Categories
                </h3>
                <div className="space-y-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full flex items-center justify-between px-4 py-2 rounded-lg text-left transition-all ${
                        selectedCategory === category.id
                          ? 'bg-purple-100 text-purple-700 font-semibold'
                          : 'hover:bg-gray-100 text-gray-600'
                      }`}
                    >
                      <span>{category.name}</span>
                      <Badge variant="secondary">{category.count}</Badge>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Newsletter */}
            <Card className="bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-2">Subscribe to Newsletter</h3>
                <p className="text-sm text-white/80 mb-4">Get the latest articles delivered to your inbox</p>
                <Input
                  placeholder="Your email"
                  className="bg-white/20 border-white/30 text-white placeholder:text-white/60 mb-3"
                />
                <Button className="w-full bg-white text-purple-600 hover:bg-gray-100">
                  Subscribe
                </Button>
              </CardContent>
            </Card>
          </aside>

          {/* Blog Posts Grid */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-6">
              <p className="text-gray-600">
                <span className="font-semibold text-gray-900">{filteredPosts.length}</span> articles found
              </p>
            </div>

            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
              </div>
            ) : filteredPosts.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-xl">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No articles found</h3>
                <p className="text-gray-500 mb-4">Try adjusting your search or filters</p>
                <Button onClick={() => { setSearchQuery(''); setSelectedCategory('All'); }}>
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {filteredPosts.map((post) => (
                  <Card
                    key={post.id}
                    className="overflow-hidden cursor-pointer hover:shadow-xl transition-all group"
                    onClick={() => navigate(`/blog/${post.id}`)}
                  >
                    {post.thumbnail && (
                      <div className="relative h-48 overflow-hidden">
                        <img src={post.thumbnail} alt={post.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                        {getCategoryName(post.category_id) && (
                          <Badge className="absolute top-4 left-4" variant="secondary">
                            {getCategoryName(post.category_id)}
                          </Badge>
                        )}
                      </div>
                    )}
                    <CardContent className="p-5">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-purple-600 transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{post.excerpt}</p>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-semibold text-xs">
                            {(post.author_name || 'A')[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{post.author_name || 'Admin'}</p>
                            <p className="text-xs text-gray-500">{formatDate(post.published_at)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <Heart className="w-4 h-4" />
                            {post.likes || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <MessageCircle className="w-4 h-4" />
                            {(post.comments || []).length}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Blog Post Detail Component
const BlogPostDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [readProgress, setReadProgress] = useState(0);
  const [tocItems, setTocItems] = useState([]);
  const [activeHeading, setActiveHeading] = useState('');
  const [copied, setCopied] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);
  usePageMeta(
    post ? (post.meta_title || post.title || 'Blog') : 'Blog',
    post ? (post.meta_description || post.excerpt || undefined) : undefined,
    !loading && !post ? { noindex: true } : { path: `/blog/${id}` },
  );
  const contentRef = useRef(null);

  // Reading progress bar
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setReadProgress(total > 0 ? Math.min(100, (scrolled / total) * 100) : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hero fade-in on mount
  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  // Fetch post + related
  useEffect(() => {
    if (!API_URL) { setLoading(false); return; }
    setLoading(true);
    setHeroVisible(false);
    setTocItems([]);
    fetch(`${API_URL}/api/public/blog/${id}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        setPost(data);
        setLikeCount(data?.likes || 0);
        if (data?.category_id) {
          fetch(`${API_URL}/api/public/blog?category_id=${data.category_id}&limit=4`)
            .then(r => r.ok ? r.json() : [])
            .then(posts => setRelatedPosts((Array.isArray(posts) ? posts : []).filter(p => p.id !== id).slice(0, 3)))
            .catch(() => {});
        }
      })
      .catch(() => setPost(null))
      .finally(() => {
        setLoading(false);
        setTimeout(() => setHeroVisible(true), 80);
      });
  }, [id]);

  // Extract TOC from content HTML string
  useEffect(() => {
    if (!post?.content) return;
    const temp = document.createElement('div');
    temp.innerHTML = sanitizeBlogHTML(post.content);
    const headings = Array.from(temp.querySelectorAll('h2, h3'));
    setTocItems(headings.map((h, i) => ({
      id: `toc-${i}`,
      text: h.textContent.trim(),
      level: parseInt(h.tagName[1]),
    })));
  }, [post?.content]);

  // Assign IDs to rendered headings + Intersection Observer
  useEffect(() => {
    if (!contentRef.current || tocItems.length === 0) return;
    const domHeadings = Array.from(contentRef.current.querySelectorAll('h2, h3'));
    domHeadings.forEach((h, i) => { h.id = `toc-${i}`; });
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length > 0) setActiveHeading(visible[0].target.id);
      },
      { rootMargin: '-5% 0% -70% 0%', threshold: 0.1 }
    );
    domHeadings.forEach(h => observer.observe(h));
    return () => observer.disconnect();
  }, [tocItems]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center py-32">
          <div className="text-center space-y-4">
            <Loader2 className="w-10 h-10 animate-spin text-violet-600 mx-auto" />
            <p className="text-gray-400 text-sm">Loading article…</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container mx-auto px-4 py-32 text-center">
          <p className="text-6xl mb-6">📭</p>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Post not found</h1>
          <p className="text-gray-500 mb-8">This article may have been removed or the URL is incorrect.</p>
          <Button onClick={() => navigate('/blog')} className="bg-violet-600 hover:bg-violet-700">
            <ArrowRight className="w-4 h-4 mr-2 rotate-180" /> Back to Blog
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const handleLike = async () => {
    if (liked) return;
    setLiked(true);
    setLikeCount(c => c + 1);
    try { await fetch(`${API_URL}/api/public/blog/${post.id}/like`, { method: 'POST' }); } catch (_) {}
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: post.title, url: window.location.href }); } catch (_) {}
    } else {
      handleCopyLink();
    }
  };

  const authorInitial = (post.author_name || 'A')[0].toUpperCase();
  const comments = post.comments || [];

  return (
    <div className="min-h-screen bg-white">
      {/* ── Reading Progress Bar ─────────────────────────────── */}
      <div className="fixed top-0 left-0 right-0 z-50 h-[3px] bg-gray-100" aria-hidden="true">
        <div
          className="h-full bg-gradient-to-r from-violet-600 to-fuchsia-500 transition-all duration-100 ease-linear"
          style={{ width: `${readProgress}%` }}
        />
      </div>

      <Header />

      {/* ── Hero ─────────────────────────────────────────────── */}
      <div className="relative h-[460px] md:h-[560px] overflow-hidden bg-gray-900">
        {post.thumbnail ? (
          <img
            src={post.thumbnail}
            alt={post.title}
            className="w-full h-full object-cover transition-transform duration-[7s] ease-out"
            style={{ transform: heroVisible ? 'scale(1.06)' : 'scale(1)' }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-violet-700 via-purple-800 to-fuchsia-700" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/10" />

        <div
          className="absolute inset-0 flex items-end transition-all duration-700 ease-out"
          style={{ opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(24px)' }}
        >
          <div className="w-full px-4 sm:px-8 pb-12" style={{ maxWidth: '1200px', margin: '0 auto' }}>
            {post.category_name && (
              <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-violet-600/80 text-white backdrop-blur-sm mb-4 border border-violet-400/20">
                {post.category_name}
              </span>
            )}
            <h1 className="text-3xl md:text-5xl font-bold text-white max-w-3xl leading-tight mb-5 tracking-tight">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-5 text-white/75 text-sm">
              <div className="flex items-center gap-2.5">
                {post.author_avatar ? (
                  <img src={post.author_avatar} alt={post.author_name} className="w-9 h-9 rounded-full object-cover ring-2 ring-white/25" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-400 flex items-center justify-center text-white font-bold text-sm ring-2 ring-white/25 flex-shrink-0">
                    {authorInitial}
                  </div>
                )}
                <span className="font-medium text-white">{post.author_name || 'iVisora'}</span>
              </div>
              {post.published_at && (
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                  {formatDate(post.published_at)}
                </span>
              )}
              {post.read_time && (
                <span className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                  {post.read_time} min read
                </span>
              )}
              {post.views > 0 && (
                <span className="flex items-center gap-1.5">
                  <Eye className="w-3.5 h-3.5 flex-shrink-0" />
                  {post.views.toLocaleString()} views
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────── */}
      <div className="px-4 sm:px-6 lg:px-8 py-12" style={{ maxWidth: '1200px', margin: '0 auto' }}>

        {/* 2-column grid: article + sidebar */}
        <div
          className="flex flex-col gap-10 lg:grid lg:items-start"
          style={{ gridTemplateColumns: 'minmax(0, 1fr) 300px', gap: '48px' }}
        >
          {/* ── LEFT: Article ──────────────────────────────── */}
          <div style={{ minWidth: 0 }}>
            <article className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12 overflow-hidden min-w-0">

              {/* Action bar */}
              <div className="flex flex-wrap items-center gap-3 mb-8 pb-8 border-b border-gray-100">
                <button
                  onClick={handleLike}
                  disabled={liked}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${
                    liked
                      ? 'bg-red-50 border-red-200 text-red-600'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-red-200 hover:text-red-500 hover:bg-red-50'
                  }`}
                >
                  <Heart className={`w-4 h-4 ${liked ? 'fill-red-500 text-red-500' : ''}`} />
                  {likeCount > 0 ? likeCount : ''} {liked ? 'Liked' : 'Like'}
                </button>

                <button
                  onClick={handleCopyLink}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${
                    copied
                      ? 'bg-green-50 border-green-200 text-green-600'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-violet-200 hover:text-violet-600 hover:bg-violet-50'
                  }`}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                  {copied ? 'Copied!' : 'Copy link'}
                </button>

                <button
                  onClick={handleShare}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border border-gray-200 text-gray-600 bg-white hover:border-violet-200 hover:text-violet-600 hover:bg-violet-50 transition-all duration-200"
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
              </div>

              {/* Excerpt */}
              {post.excerpt && (
                <p className="text-xl text-gray-600 leading-relaxed mb-8 font-light border-l-4 border-violet-400 pl-5 italic">
                  {post.excerpt}
                </p>
              )}

              {/* Main content */}
              {post.content && (
                <div
                  ref={contentRef}
                  className="prose prose-lg prose-violet max-w-none break-words
                    prose-headings:font-bold prose-headings:text-gray-900 prose-headings:tracking-tight prose-headings:break-words
                    prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                    prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                    prose-p:text-gray-700 prose-p:leading-8 prose-p:mb-6 prose-p:break-words
                    prose-a:text-violet-600 prose-a:no-underline hover:prose-a:underline prose-a:break-all
                    prose-strong:text-gray-900 prose-strong:font-semibold
                    prose-blockquote:border-l-4 prose-blockquote:border-violet-400 prose-blockquote:bg-violet-50 prose-blockquote:rounded-r-lg prose-blockquote:py-1 prose-blockquote:not-italic
                    prose-code:bg-gray-100 prose-code:text-violet-700 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono prose-code:before:content-none prose-code:after:content-none prose-code:break-all
                    prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-xl prose-pre:shadow-lg prose-pre:overflow-x-auto
                    prose-img:rounded-xl prose-img:shadow-md prose-img:my-8 prose-img:max-w-full"
                  style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                  dangerouslySetInnerHTML={{ __html: sanitizeBlogHTML(post.content) }}
                />
              )}

              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="mt-10 pt-8 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5" /> Tags
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map(tag => (
                      <span key={tag} className="px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-600 hover:bg-violet-100 hover:text-violet-700 cursor-pointer transition-colors duration-150">
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Bottom action + author credit */}
              <div className="mt-10 pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
                <div className="flex items-center gap-3">
                  {post.author_avatar ? (
                    <img src={post.author_avatar} alt={post.author_name} className="w-11 h-11 rounded-full object-cover" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-400 flex items-center justify-center text-white font-bold flex-shrink-0">
                      {authorInitial}
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{post.author_name || 'iVisora'}</p>
                    {post.author_bio && <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{post.author_bio}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <button
                    onClick={handleLike}
                    disabled={liked}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all ${liked ? 'bg-red-50 border-red-200 text-red-600' : 'border-gray-200 text-gray-600 hover:border-red-200 hover:text-red-500'}`}
                  >
                    <Heart className={`w-4 h-4 ${liked ? 'fill-red-500 text-red-500' : ''}`} />
                    {likeCount > 0 ? likeCount : 'Like'}
                  </button>
                  <button onClick={handleCopyLink} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all ${copied ? 'bg-green-50 border-green-200 text-green-600' : 'border-gray-200 text-gray-600 hover:border-violet-200 hover:text-violet-600'}`}>
                    {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
            </article>

            {/* ── Comments ──────────────────────────────────── */}
            <div className="mt-8 bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
              <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-violet-600" />
                Comments
                <span className="ml-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs font-medium">{comments.length}</span>
              </h3>

              {/* Input */}
              <div className="mb-8">
                <Textarea
                  placeholder="Share your thoughts…"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="mb-3 resize-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                  rows={3}
                />
                <Button className="bg-violet-600 hover:bg-violet-700 text-white">
                  <Send className="w-4 h-4 mr-2" />
                  Post Comment
                </Button>
              </div>

              {/* List */}
              {comments.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <MessageCircle className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No comments yet — be the first!</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {comments.map((comment, idx) => (
                    <div key={comment.id || idx} className="flex gap-4">
                      <div className="w-9 h-9 rounded-full bg-violet-100 flex items-center justify-center text-violet-600 font-semibold text-sm flex-shrink-0">
                        {(comment.author_name || 'A')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-1.5">
                          <p className="font-semibold text-gray-900 text-sm">{comment.author_name || 'Anonymous'}</p>
                          <p className="text-xs text-gray-400">{formatDate(comment.created_at)}</p>
                        </div>
                        <p className="text-gray-700 text-sm leading-relaxed">{comment.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── RIGHT: Sticky Sidebar ───────────────────────── */}
          <aside style={{ position: 'sticky', top: '100px' }} className="space-y-6 hidden lg:block">

            {/* Author card */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-center">
              {post.author_avatar ? (
                <img src={post.author_avatar} alt={post.author_name} className="w-20 h-20 rounded-full object-cover mx-auto mb-4 ring-4 ring-violet-100" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4 ring-4 ring-violet-100">
                  {authorInitial}
                </div>
              )}
              <h4 className="font-bold text-gray-900 text-base mb-1">{post.author_name || 'iVisora Team'}</h4>
              {post.author_bio ? (
                <p className="text-sm text-gray-500 leading-relaxed mb-4">{post.author_bio}</p>
              ) : (
                <p className="text-sm text-gray-400 mb-4">Content creator at iVisora</p>
              )}
              <button className="w-full py-2 px-4 rounded-full text-sm font-semibold bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:opacity-90 transition-opacity">
                Follow
              </button>
            </div>

            {/* Table of Contents */}
            {tocItems.length > 1 && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h4 className="font-bold text-gray-900 text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-violet-600" />
                  Contents
                </h4>
                <nav className="space-y-1">
                  {tocItems.map(item => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      className={`flex items-start gap-1.5 text-sm py-1 rounded transition-colors duration-150 ${
                        item.level === 3 ? 'pl-4' : 'pl-0'
                      } ${
                        activeHeading === item.id
                          ? 'text-violet-600 font-medium'
                          : 'text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      <ChevronRight className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 transition-colors ${activeHeading === item.id ? 'text-violet-600' : 'text-gray-300'}`} />
                      <span className="leading-snug line-clamp-2">{item.text}</span>
                    </a>
                  ))}
                </nav>
              </div>
            )}

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h4 className="font-bold text-gray-900 text-sm uppercase tracking-wider mb-4">Related Articles</h4>
                <div className="space-y-4">
                  {relatedPosts.map(related => (
                    <button
                      key={related.id}
                      onClick={() => navigate(`/blog/${related.id}`)}
                      className="flex gap-3 w-full text-left group"
                    >
                      {related.thumbnail ? (
                        <img
                          src={related.thumbnail}
                          alt={related.title}
                          className="w-16 h-14 rounded-lg object-cover flex-shrink-0 group-hover:opacity-90 transition-opacity"
                        />
                      ) : (
                        <div className="w-16 h-14 rounded-lg bg-gradient-to-br from-violet-100 to-fuchsia-100 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 group-hover:text-violet-600 line-clamp-2 leading-snug transition-colors">
                          {related.title}
                        </p>
                        {related.read_time && (
                          <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {related.read_time} min
                          </p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h4 className="font-bold text-gray-900 text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Tag className="w-4 h-4 text-violet-600" /> Tags
                </h4>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map(tag => (
                    <span key={tag} className="px-3 py-1 rounded-full text-xs bg-gray-100 text-gray-600 hover:bg-violet-100 hover:text-violet-700 cursor-pointer transition-colors">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>

      {/* ── CTA ──────────────────────────────────────────────── */}
      <section className="mt-16 bg-gradient-to-br from-violet-600 via-purple-700 to-fuchsia-600 py-20">
        <div className="container mx-auto px-4 text-center max-w-2xl">
          <p className="text-violet-200 text-sm font-medium mb-3 uppercase tracking-wider">Start creating</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Bring your ideas to life</h2>
          <p className="text-white/70 mb-8 leading-relaxed">
            Create stunning visuals with iVisora — free forever.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/editor')}
            className="bg-white text-violet-700 hover:bg-gray-50 font-semibold px-8 shadow-xl hover:shadow-2xl transition-all duration-200 hover:-translate-y-0.5"
          >
            Start Designing Free
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Main Blog Page Component
const BlogPage = () => {
  const { id } = useParams();
  
  if (id) {
    return <BlogPostDetail />;
  }
  
  return <BlogList />;
};

export default BlogPage;
